import * as Y from 'yjs';
import { fillFragment, fragmentToJSON, plainToRich, richToPlain, type RichDoc } from './rich';
import { TABLE_ROW_H } from './schema';
import { sortedTaskEntries } from './tasks';
import { generateNKeysBetween } from 'fractional-indexing';

export const TEXT_TYPES = ['text', 'sticky', 'shape'] as const;
export const isTextType = (t: unknown): t is (typeof TEXT_TYPES)[number] => (TEXT_TYPES as readonly unknown[]).includes(t);

/**
 * Plain JSON view of an object's Y.Map, the only shape the schema validates: rich `content` becomes ProseMirror JSON
 * (and `text` its plain text), table rows and columns become arrays.
 */
export function readObject(m: Y.Map<unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of m.entries()) {
    if (v instanceof Y.XmlFragment) out[k] = fragmentToJSON(v);
    else if (k === 'rows' && v instanceof Y.Array) {
      out[k] = (v.toArray() as unknown[]).map((row) => {
        if (!(row instanceof Y.Map)) return row;
        const cells: Record<string, string> = {};
        for (const [c, t] of row.entries()) if (c !== 'id') cells[c] = String(t);
        return { id: row.get('id'), cells };
      });
    } else if (k === 'items' && v instanceof Y.Map) {
      // Task lists: an ordered array in display order, without the internal order keys (Ruling 2).
      out[k] = sortedTaskEntries(v).map(([id, item]) => {
        const task: Record<string, unknown> = { id };
        for (const [f, x] of item.entries()) if (f !== 'pos' && f !== 'id') task[f] = x instanceof Y.AbstractType ? x.toJSON() : x;
        return task;
      });
    } else if (v instanceof Y.AbstractType) out[k] = v.toJSON();
    else out[k] = v;
  }
  // A table's size follows its columns and rows (fixed row height), whoever wrote w/h last.
  if (out.type === 'table' && Array.isArray(out.cols) && Array.isArray(out.rows)) {
    out.w = Math.max(1, (out.cols as { w?: unknown }[]).reduce((s: number, c) => s + (typeof c.w === 'number' ? c.w : 0), 0));
    out.h = Math.max(1, out.rows.length * TABLE_ROW_H);
  }
  if (isTextType(out.type)) {
    if (out.content && typeof out.content === 'object') out.text = richToPlain(out.content as RichDoc);
    else {
      out.content = null;
      out.text = typeof out.text === 'string' ? out.text : '';
    }
  }
  return out;
}

/**
 * Writes `data` into `m`, which must already belong to a document. Text types always get a fragment (from `content`,
 * else from `text`) and never a `text` key; tables get Y types so cells merge character by character.
 */
export function writeObject(m: Y.Map<unknown>, data: Record<string, unknown>): void {
  const text = isTextType(data.type);
  const tasks = data.type === 'tasklist';
  for (const [k, v] of Object.entries(data)) {
    if (text && (k === 'content' || k === 'text')) continue;
    if (tasks && k === 'title') {
      m.set('title', new Y.Text(typeof v === 'string' ? v : ''));
      continue;
    }
    if (tasks && k === 'items' && Array.isArray(v)) {
      const items = new Y.Map<Y.Map<unknown>>();
      m.set('items', items);
      const list = v as Record<string, unknown>[];
      const keys = generateNKeysBetween(null, null, list.length);
      list.forEach((t, i) => {
        const item = new Y.Map<unknown>();
        items.set(String(t.id), item);
        item.set('pos', keys[i]);
        for (const [f, x] of Object.entries(t)) {
          if (f === 'id' || x === undefined) continue;
          item.set(f, f === 'text' ? new Y.Text(typeof x === 'string' ? x : '') : x);
        }
      });
      continue;
    }
    if (k === 'rows' && Array.isArray(v)) {
      const rows = new Y.Array<Y.Map<unknown>>();
      m.set('rows', rows);
      rows.push((v as { id: string; cells: Record<string, string> }[]).map(() => new Y.Map<unknown>()));
      (v as { id: string; cells: Record<string, string> }[]).forEach((r, i) => {
        const row = rows.get(i);
        row.set('id', r.id);
        for (const [c, s] of Object.entries(r.cells)) row.set(c, new Y.Text(s));
      });
    } else if (k === 'cols' && Array.isArray(v)) {
      const cols = new Y.Array<unknown>();
      m.set('cols', cols);
      cols.push(v.map((c) => ({ ...(c as object) })));
    } else m.set(k, v);
  }
  if (text) {
    const f = new Y.XmlFragment();
    m.set('content', f);
    fillFragment(f, (data.content as RichDoc | null | undefined) ?? plainToRich(typeof data.text === 'string' ? data.text : ''));
  }
}
